<!--
  Help Block is a component that can activate the audio or show the answer.
  Usage: <Breath @answer="handleAnswer" />
-->
<script setup lang="ts">
import { onUnmounted, ref } from "vue"
import Button from "@/components/ui/button/Button.vue"
import { useHelpStore } from "./help.store"
import { storeToRefs } from "pinia"
import { useI18nStore } from "@/stores/i18n"

const { stopAudio, removeAudioEventListener, startAudioSequence, toggleAudio } =
  useHelpStore()

const { loading: isLocaleLoading } = storeToRefs(useI18nStore())

const {
  name,
  hideAudioBox,
  isVolumeActive,
  isReady,
  showPrivateSafariTooltip,
} = storeToRefs(useHelpStore())

const showTooltip = ref(false)
const timeoutTooltip = ref<number | undefined>(undefined)

function handleAudio() {
  if (
    name.value === "Swipe" ||
    name.value === "Drive" ||
    name.value === "Brain" ||
    name.value === "Move" ||
    name.value === "MoveBis"
  ) {
    if (!isVolumeActive.value) {
      startAudioSequence()
      toggleAudio(true)
    } else {
      stopAudio()
      toggleAudio(false)
    }
  }
}

function handleTooltip() {
  if (timeoutTooltip.value) clearTimeout(timeoutTooltip.value)
  showTooltip.value = true
  timeoutTooltip.value = setTimeout(() => {
    showTooltip.value = false
  }, 5000) as unknown as number
}

onUnmounted(() => {
  removeAudioEventListener()
  clearTimeout(timeoutTooltip.value)
})
</script>

<template>
  <div class="group relative whitespace-nowrap">
    <Button
      v-if="isReady && !hideAudioBox"
      :loading="isLocaleLoading"
      variant="tertiary"
      @click="handleAudio()"
      class="group border-grey-lighten-3! text-grey h-11 w-11 gap-2 border! border-solid! px-3.5 py-2 text-lg font-normal"
      :aria-label="
        isVolumeActive
          ? $t('accessibility.assessment.audio.off')
          : $t('accessibility.assessment.audio.on')
      "
    >
      <FaIcon v-if="isVolumeActive" icon="fa-solid fa-volume" class="h-4" />
      <FaIcon v-else icon="fa-solid fa-volume-slash" class="h-4" />

      <span
        class="bg-grey absolute right-14 z-50 hidden rounded p-3 px-3 py-2 text-center text-xs text-white group-hover:block group-focus:block"
      >
        {{ $t("general.audio.name") }}
      </span>
    </Button>
    <Button
      v-else-if="!isReady && !hideAudioBox && showPrivateSafariTooltip"
      @click="handleTooltip()"
      variant="tertiary"
      class="group border-grey-lighten-3! text-grey h-11 w-11 gap-2 border! border-solid! px-3.5 py-2 text-lg font-normal"
    >
      <FaIcon icon="fa-solid fa-volume-slash" class="h-4 opacity-50" />
      <span
        v-if="showTooltip"
        class="bg-grey absolute top-[115%] z-50 w-56 rounded p-3 px-3 py-2 text-center text-xs whitespace-normal text-white opacity-50 lg:top-0 lg:right-14"
      >
        {{ $t("error.browser.audio_safari") }}
      </span>
    </Button>
  </div>
</template>

<style lang="css" scoped>
.arabic .fa-volume-slash,
.arabic .fa-volume {
  transform: rotateY(180deg);
}
</style>
